import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiAlertTriangle,
  FiPackage,
  FiArrowLeft,
  FiPlus,
  FiTrash2,
  FiCheck,
  FiChevronRight,
} from "react-icons/fi";
import escalationService from "../../services/escalationService";

const ESCALATION_CATEGORIES = [
  "Administrative & Policy Exception",
  "Academic & Curriculum Regulation",
  "Disciplinary & Student Affairs",
  "University / Board / Affiliation Matters",
  "Legal, Statutory & Compliance",
  "Finance & Special Budget Approval",
  "Faculty & Staffing Requisition",
  "Campus Infrastructure & Security",
  "Other Unresolved Matter",
];

const RESOURCE_CATEGORIES = [
  "IT & Electronics (Computers, Printers, Projectors, Networking)",
  "Laboratory Equipment, Apparatus & Chemicals",
  "Classroom Furniture, Desks & Whiteboards",
  "Library Books, Journals & Digital Subscriptions",
  "Software Licenses, ERP & Digital Tools",
  "Campus Facilities, Sports & Gym Equipment",
  "Building Maintenance, Civil & Electrical Repairs",
  "Office Admin Assets & Printing Consumables",
  "Other Resource / Equipment",
];

export default function EscalationForm() {
  const navigate = useNavigate();

  const [type, setType] = useState("resource_request"); // 'escalation' or 'resource_request'
  const [category, setCategory] = useState(RESOURCE_CATEGORIES[0]);
  const [priority, setPriority] = useState("medium");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [impactJustification, setImpactJustification] = useState("");
  const [requiredByDate, setRequiredByDate] = useState("");

  // Resource Request itemized list
  const [itemsRequested, setItemsRequested] = useState([
    { itemName: "", quantity: 1, estimatedCost: 0, specifications: "", urgencyReason: "" },
  ]);

  const [submitting, setSubmitting] = useState(false);

  // Handle change of type
  const handleTypeChange = (newType) => {
    setType(newType);
    if (newType === "escalation") {
      setCategory(ESCALATION_CATEGORIES[0]);
    } else {
      setCategory(RESOURCE_CATEGORIES[0]);
    }
  };

  // Add Item to Resource Requisition
  const addItemRow = () => {
    setItemsRequested((prev) => [
      ...prev,
      { itemName: "", quantity: 1, estimatedCost: 0, specifications: "", urgencyReason: "" },
    ]);
  };

  // Remove Item row
  const removeItemRow = (index) => {
    if (itemsRequested.length === 1) {
      toast.error("At least one item is required for resource request.");
      return;
    }
    setItemsRequested((prev) => prev.filter((_, i) => i !== index));
  };

  // Update item field
  const updateItemField = (index, field, value) => {
    setItemsRequested((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  // Calculate Total Estimated Budget
  const totalBudget = itemsRequested.reduce((sum, item) => {
    const qty = Number(item.quantity) || 1;
    const cost = Number(item.estimatedCost) || 0;
    return sum + qty * cost;
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Please enter a subject / title.");
      return;
    }

    if (!description.trim()) {
      toast.error("Please enter a detailed description.");
      return;
    }

    if (type === "resource_request") {
      for (let i = 0; i < itemsRequested.length; i++) {
        if (!itemsRequested[i].itemName.trim()) {
          toast.error(`Please specify the Item Name for item #${i + 1}.`);
          return;
        }
        if (Number(itemsRequested[i].quantity) < 1) {
          toast.error(`Quantity for item #${i + 1} must be at least 1.`);
          return;
        }
      }
    }

    try {
      setSubmitting(true);
      const payload = {
        type,
        category,
        priority,
        title: title.trim(),
        description: description.trim(),
        impactJustification: impactJustification.trim(),
        requiredByDate: requiredByDate || null,
        itemsRequested: type === "resource_request" ? itemsRequested : [],
      };

      const res = await escalationService.create(payload);
      const createdId = res?.data?._id || res?._id || res?.data?.id || res?.id;
      if (res?.success || createdId) {
        toast.success(
          type === "escalation"
            ? "Matter escalated to Super Admin successfully!"
            : "Resource request submitted to Super Admin successfully!"
        );
        if (createdId) {
          navigate(`/escalations/${createdId}`);
        } else {
          navigate("/escalations");
        }
      } else {
        toast.error(res?.message || "Failed to submit request.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container" style={{ maxWidth: 960, margin: "0 auto", padding: "1.5rem 1rem" }}>
      {/* ── Breadcrumb ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#64748b", fontSize: "13px", marginBottom: "1rem" }}>
        <Link to="/dashboard" style={{ color: "#64748b" }}>Dashboard</Link>
        <FiChevronRight size={13} />
        <Link to="/escalations" style={{ color: "#64748b" }}>Escalations & Requests</Link>
        <FiChevronRight size={13} />
        <span style={{ color: "var(--text, #1e293b)", fontWeight: 600 }}>Create New</span>
      </div>

      {/* ── Page Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: 800, margin: 0, color: "var(--text, #0f172a)" }}>
            Submit Escalation or Resource Request
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "var(--text-muted, #64748b)" }}>
            Report matters requiring central resolution or submit requisitions for equipment and facilities.
          </p>
        </div>
        <Link to="/escalations" className="btn btn-outline" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <FiArrowLeft size={14} /> Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ── Request Type Selector ── */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#334155", marginBottom: "0.6rem" }}>
            Select Request Type <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            {/* Option 1: Resource Request */}
            <div
              onClick={() => handleTypeChange("resource_request")}
              style={{
                padding: "1.1rem 1.25rem",
                borderRadius: "10px",
                border: type === "resource_request" ? "2px solid #3b82f6" : "1px solid #e2e8f0",
                backgroundColor: type === "resource_request" ? "#e8f1f8" : "#fff",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "6px" }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: type === "resource_request" ? "#3b82f6" : "#f1f5f9",
                    color: type === "resource_request" ? "#fff" : "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiPackage size={17} />
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 700, color: type === "resource_request" ? "#1e3a8a" : "#1e293b" }}>
                    Resource / Equipment Requisition
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    Request computers, printers, lab apparatus, furniture, licenses, or campus maintenance.
                  </div>
                </div>
              </div>
            </div>

            {/* Option 2: Matter Escalation */}
            <div
              onClick={() => handleTypeChange("escalation")}
              style={{
                padding: "1.1rem 1.25rem",
                borderRadius: "10px",
                border: type === "escalation" ? "2px solid #f59e0b" : "1px solid #e2e8f0",
                backgroundColor: type === "escalation" ? "#fffbeb" : "#fff",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "6px" }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: type === "escalation" ? "#f59e0b" : "#f1f5f9",
                    color: type === "escalation" ? "#fff" : "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiAlertTriangle size={17} />
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 700, color: type === "escalation" ? "#92400e" : "#1e293b" }}>
                    Matter Escalation
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    Escalate issues that cannot be resolved at college level (administrative, legal, university, policy).
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Main Details Card ── */}
        <div
          className="card"
          style={{
            padding: "1.5rem",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            marginBottom: "1.5rem",
          }}
        >
          <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "1rem" }}>
            General Information
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
            {/* Category */}
            <div>
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12.5px" }}>
                Category <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="form-control"
                required
              >
                {(type === "escalation" ? ESCALATION_CATEGORIES : RESOURCE_CATEGORIES).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12.5px" }}>
                Priority Level <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="form-control"
                required
              >
                <option value="low">Low - Routine matters</option>
                <option value="medium">Medium - Standard operational requirement</option>
                <option value="high">High - Impacting academic or admin activities</option>
                <option value="urgent">Urgent - Critical / Inspection / Time-sensitive</option>
              </select>
            </div>
          </div>

          {/* Subject / Title */}
          <div style={{ marginBottom: "1.25rem" }}>
            <label className="form-label" style={{ fontWeight: 600, fontSize: "12.5px" }}>
              Subject / Title <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder={
                type === "resource_request"
                  ? "e.g., Requirement of 10 Computers and 2 Laser Printers for Pharmacy Lab"
                  : "e.g., University Inspection Query regarding Affiliation of New Batch"
              }
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Detailed Description */}
          <div style={{ marginBottom: "1.25rem" }}>
            <label className="form-label" style={{ fontWeight: 600, fontSize: "12.5px" }}>
              {type === "escalation" ? "Matter Description & Facts" : "Purpose & Requisition Details"}{" "}
              <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <textarea
              className="form-control"
              rows={4}
              placeholder={
                type === "escalation"
                  ? "Provide full details: what occurred, timeline, students/staff involved, prior attempts to resolve..."
                  : "Explain why these resources/equipment are needed, which department/lab will use them, and current status..."
              }
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          {/* Impact / Reason College cannot resolve */}
          <div style={{ marginBottom: "1.25rem" }}>
            <label className="form-label" style={{ fontWeight: 600, fontSize: "12.5px" }}>
              {type === "escalation"
                ? "Reason College Cannot Resolve at Principal/Admin Level"
                : "Operational / Academic Impact & Urgency Justification"}
            </label>
            <textarea
              className="form-control"
              rows={2}
              placeholder={
                type === "escalation"
                  ? "e.g., Requires central trust policy decision, legal intervention, or high-level university appeal..."
                  : "e.g., Necessary for upcoming PCI/AICTE inspection or commencement of practical semester exams..."
              }
              value={impactJustification}
              onChange={(e) => setImpactJustification(e.target.value)}
            />
          </div>

          {/* Required By Date (for Resource Requests) */}
          {type === "resource_request" && (
            <div style={{ maxWidth: 300 }}>
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12.5px" }}>
                Required By Date (Optional)
              </label>
              <input
                type="date"
                className="form-control"
                value={requiredByDate}
                onChange={(e) => setRequiredByDate(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* ── Itemized Resources Table (Only for Resource Request) ── */}
        {type === "resource_request" && (
          <div
            className="card"
            style={{
              padding: "1.5rem",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              marginBottom: "1.5rem",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
                flexWrap: "wrap",
                gap: "0.5rem",
              }}
            >
              <div>
                <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                  Itemized Resource Requisition
                </div>
                <div style={{ fontSize: "12px", color: "#64748b" }}>
                  Specify each piece of equipment or asset required with estimated costs and specifications.
                </div>
              </div>
              <button
                type="button"
                onClick={addItemRow}
                className="btn btn-outline"
                style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}
              >
                <FiPlus size={13} /> Add Another Item
              </button>
            </div>

            <div className="table-wrapper" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", fontSize: "12px" }}>
                    <th style={{ width: "25%" }}>Item / Equipment Name *</th>
                    <th style={{ width: "12%" }}>Qty *</th>
                    <th style={{ width: "18%" }}>Est. Unit Cost (₹)</th>
                    <th style={{ width: "22%" }}>Specifications / Model</th>
                    <th style={{ width: "18%" }}>Requirement Reason</th>
                    <th style={{ width: "5%", textAlign: "center" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {itemsRequested.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Core i5 Desktop PC"
                          value={item.itemName}
                          onChange={(e) => updateItemField(index, "itemName", e.target.value)}
                          required
                          style={{ fontSize: "12.5px" }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={item.quantity}
                          onChange={(e) => updateItemField(index, "quantity", e.target.value)}
                          required
                          style={{ fontSize: "12.5px" }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="100"
                          className="form-control"
                          placeholder="₹ 0"
                          value={item.estimatedCost || ""}
                          onChange={(e) => updateItemField(index, "estimatedCost", e.target.value)}
                          style={{ fontSize: "12.5px" }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. 16GB RAM, SSD"
                          value={item.specifications}
                          onChange={(e) => updateItemField(index, "specifications", e.target.value)}
                          style={{ fontSize: "12.5px" }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. For Pharma Lab 2"
                          value={item.urgencyReason}
                          onChange={(e) => updateItemField(index, "urgencyReason", e.target.value)}
                          style={{ fontSize: "12.5px" }}
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {itemsRequested.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItemRow(index)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#ef4444",
                              cursor: "pointer",
                              padding: "4px",
                            }}
                            title="Remove item"
                          >
                            <FiTrash2 size={15} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total Budget Summary */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "1rem",
                marginTop: "1rem",
                paddingTop: "0.75rem",
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <div style={{ fontSize: "13px", color: "#64748b" }}>
                Total Items: <strong>{itemsRequested.reduce((s, i) => s + (Number(i.quantity) || 1), 0)}</strong>
              </div>
              <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                Total Estimated Budget:{" "}
                <span style={{ color: "#003b67", fontSize: "16px" }}>
                  ₹{totalBudget.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Form Actions ── */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate("/escalations")}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 140, justifyContent: "center" }}
          >
            {submitting ? (
              <>
                <div className="spinner-sm" /> Submitting…
              </>
            ) : (
              <>
                <FiCheck size={16} />
                {type === "escalation" ? "Submit Escalation" : "Submit Resource Request"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
